// Crave Score calculator and configuration
// Easily adjust weights and goals per metric here.
import i18n from "@/i18n/i18n";

type Direction = "higher" | "lower";

export interface MetricConfig {
  id: string;
  label: string;
  enabled?: boolean; // set to false to exclude this metric from the score and card display
  weight: number; // relative weight; key levers should have larger weights
  goal: number; // target value in the same unit as numericValue
  direction: Direction; // 'higher' is better or 'lower' is better
  // Optional: custom normalizer; return value in [-1, 1] where 0 = at goal
  normalizer?: (value: number, goal: number, direction: Direction) => number;
}

export interface MetricInput {
  id: string;
  label?: string;
  rawValue: string | number | null | undefined;
}

export interface MetricContribution {
  id: string;
  label: string;
  rawValue: string | number | null;
  numericValue: number | null;
  goal: number;
  direction: Direction;
  weight: number;
  normalized: number; // [-1, 1]
  points: number; // contribution to final 0..100 score (can be negative)
}

export interface CraveScoreResult {
  score: number; // 0..100
  contributions: MetricContribution[];
}

// ---------- Parsing helpers ----------
const PERCENT_RE = /(-?\d+(?:\.\d+)?)%/;
const COMPACT_RE = /(-?\d+(?:\.\d+)?)([KMB])/i;

const parseNumberLoose = (raw: string | number | null | undefined): number | null => {
  if (raw == null) return null;
  if (typeof raw === "number" && Number.isFinite(raw)) return raw;
  if (typeof raw !== "string") return null;
  const s = raw.trim();
  if (!s) return null;

  // 1) Duration like 1m30s or 45s
  const dur = parseDurationSeconds(s);
  if (dur != null) return dur;

  // 2) Percentage like 12%
  const pm = s.match(PERCENT_RE);
  if (pm) {
    const num = Number(pm[1]);
    return Number.isFinite(num) ? num : null;
  }

  // 3) Compact number 1.2K / 3.4M / 2B
  const cm = s.match(COMPACT_RE);
  if (cm) {
    const base = Number(cm[1]);
    if (!Number.isFinite(base)) return null;
    const factor =
      cm[2].toUpperCase() === "K" ? 1_000 : cm[2].toUpperCase() === "M" ? 1_000_000 : 1_000_000_000;
    return base * factor;
  }

  // 4) Currency like $1,234 or $1.2K -> strip non-numeric except . - ,
  const cleaned = s.replace(/[^0-9.,\-]/g, "").replace(/,/g, "");
  if (!cleaned) return null;
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : null;
};

const parseDurationSeconds = (raw: string): number | null => {
  const m = raw.match(/(?:(\d+)m)?\s*(\d+)s/i);
  if (!m) return null;
  const minutes = m[1] ? Number(m[1]) : 0;
  const seconds = Number(m[2]);
  if (!Number.isFinite(seconds) || !Number.isFinite(minutes)) return null;
  return minutes * 60 + seconds;
};

// Default normalizer: scale difference relative to goal.
// - For higher-is-better: (value - goal) / goal
// - For lower-is-better: (goal - value) / goal
// Then clamp to [-1, 1]
const defaultNormalizer = (value: number, goal: number, direction: Direction): number => {
  if (!Number.isFinite(value) || !Number.isFinite(goal) || goal === 0) return 0;
  const diff = direction === "higher" ? value - goal : goal - value;
  const norm = diff / Math.abs(goal);
  return Math.max(-1, Math.min(1, norm));
};

// Calculate overall score on 0..100 scale; 80 means at goal on average.
export const CRAVE_BASELINE = 70;
const CRAVE_AMPLITUDE = 40; // maximum swing above/below baseline when all metrics are perfectly above/below goal
export const calculateCraveScore = (
  configs: MetricConfig[],
  inputs: MetricInput[],
): CraveScoreResult => {
  // Map for quick lookup of input by id
  const byId = new Map<string, MetricInput>();
  for (const inp of inputs) byId.set(inp.id, inp);

  // Only include metrics that are enabled (default true if omitted)
  const active = configs.filter((c) => c.enabled !== false);
  const totalWeight = active.reduce((acc, c) => acc + (c.weight || 0), 0) || 1;

  const contributions: MetricContribution[] = active.map((cfg) => {
    const input = byId.get(cfg.id);
    const raw = input?.rawValue ?? null;
    const numeric = parseNumberLoose(raw);
    const normalizer = cfg.normalizer || defaultNormalizer;
    const normalized = numeric == null ? 0 : normalizer(numeric, cfg.goal, cfg.direction);
    // Convert normalized [-1,1] to points relative to final score contribution
    // Final mapping is: score = BASELINE + AMPLITUDE * sum(weight*normalized)/sum(weights)
    const points = CRAVE_AMPLITUDE * ((cfg.weight * normalized) / totalWeight);

    return {
      id: cfg.id,
      label: input?.label || cfg.label,
      rawValue: raw,
      numericValue: numeric,
      goal: cfg.goal,
      direction: cfg.direction,
      weight: cfg.weight,
      normalized,
      points,
    };
  });

  const totalNormalizedWeighted = contributions.reduce(
    (acc, c) => acc + c.weight * c.normalized,
    0,
  );
  let score = Math.max(
    0,
    Math.min(100, CRAVE_BASELINE + CRAVE_AMPLITUDE * (totalNormalizedWeighted / totalWeight)),
  );

  // Business rule: if there are no sessions (0) OR no session data is present, the crave score should be 0.
  // This applies when 'sessions' parses to 0, or when it is missing/undefined/unparseable (null).
  const sessionsInput = byId.get("sessions");
  const sessionsNumeric = parseNumberLoose(sessionsInput?.rawValue);
  if (sessionsNumeric === 0 || sessionsNumeric == null) {
    score = 0;
  }

  return { score, contributions };
};

// Default configuration for the Store Performance page metrics.
// Tweak weights/goals here as needed.

// Custom normalizer for Experience Rating (0..5 scale):
// - Below or at goal uses default scaling (relative to goal) yielding [-1, 0]
// - Above goal scales so that 5.0 maps to full credit (+1)
const satisfactionNormalizer = (value: number, goal: number, direction: Direction): number => {
  if (!Number.isFinite(value) || !Number.isFinite(goal)) return 0;
  if (direction !== "higher") return defaultNormalizer(value, goal, direction);
  const maxRating = 5.0;

  // At or below goal: same behavior as default (negative up to -1 at 0)
  if (value <= goal) {
    if (goal === 0) return 0;
    const norm = (value - goal) / Math.abs(goal);
    return Math.max(-1, Math.min(1, norm));
  }

  // Above goal: scale to reach +1 at max rating (5.0)
  const denom = maxRating - goal;
  if (denom <= 0) return 1; // if goal >= max, any above-goal value gets full credit
  const norm = (Math.min(value, maxRating) - goal) / denom;
  return Math.max(-1, Math.min(1, norm));
};

// Factory: builds a normalizer where full credit (+1) is awarded at goal × multiplier for higher-is-better metrics.
// Below or at goal behaves like the default normalizer. Lower-is-better delegates to default.
const createAboveGoalNormalizer = (multiplier: number) => {
  return (value: number, goal: number, direction: Direction): number => {
    if (!Number.isFinite(value) || !Number.isFinite(goal)) return 0;
    if (direction !== "higher") return defaultNormalizer(value, goal, direction);
    if (goal === 0) return 0;

    // At or below goal: same as default
    if (value <= goal) {
      const norm = (value - goal) / Math.abs(goal);
      return Math.max(-1, Math.min(1, norm));
    }

    const cap = goal * multiplier;
    const denom = cap - goal; // = goal * (multiplier - 1)
    if (!Number.isFinite(denom) || denom <= 0) return 1; // if multiplier<=1 or invalid, any above-goal value gets full credit

    const norm = (Math.min(value, cap) - goal) / denom;
    return Math.max(-1, Math.min(1, norm));
  };
};

// Tunable multiplier for UPS: full credit at 1.5× goal by default.
const UPS_FULL_CREDIT_MULTIPLIER = 1.5;

export const defaultCraveScoreConfig: MetricConfig[] = [
  // Key Performance Levers (heaviest weights)
  {
    id: "sessions",
    label: i18n.t("storeComparison.fields.fittingRoomSessions"),
    enabled: false,
    weight: 1.0,
    goal: 100,
    direction: "higher",
  },
  {
    id: "ups",
    label: "Units Per Session",
    weight: 3,
    goal: 3.0,
    direction: "higher",
    normalizer: createAboveGoalNormalizer(UPS_FULL_CREDIT_MULTIPLIER),
  },
  { id: "missedPct", label: "Missed Requests %", weight: 3, goal: 6, direction: "lower" },
  { id: "avgResponseSec", label: "Avg Response Time (s)", weight: 3, goal: 90, direction: "lower" },

  // Requests Breakdown
  {
    id: "fulfilledPct",
    label: "Fulfilled Requests %",
    enabled: false,
    weight: 2.0,
    goal: 85,
    direction: "higher",
  },
  { id: "cantFindPct", label: "Can't Finds %", weight: 1.5, goal: 5, direction: "lower" },
  {
    id: "canceledPct",
    label: "Cancelled Requests %",
    enabled: false,
    weight: 1,
    goal: 3,
    direction: "lower",
  },
  {
    id: "totalRequests",
    label: "Total Requests",
    enabled: false,
    weight: 1.0,
    goal: 100,
    direction: "higher",
  },

  // Session Breakdown
  {
    id: "dwellSec",
    label: "Dwell Time (s)",
    enabled: false,
    weight: 1.0,
    goal: 300,
    direction: "higher",
  },
  {
    id: "namedShoppersPct",
    label: "Named Shoppers %",
    enabled: false,
    weight: 1.0,
    goal: 25,
    direction: "higher",
  },
  {
    id: "satisfaction",
    label: "Experience Rating",
    weight: 2,
    goal: 4.7,
    direction: "higher",
    normalizer: satisfactionNormalizer,
  },
];

// Device-type specific configurations. Initially identical to default, but isolated for tuning per device.
export type DeviceTypeForCrave =
  | "all"
  | "FITTING_ROOM"
  | "IN_AISLE"
  | "MOBILE_PICKUP"
  | "RFID_CENTRAL";

const deviceCraveScoreConfigs: Record<DeviceTypeForCrave, MetricConfig[]> = {
  all: [...defaultCraveScoreConfig],
  RFID_CENTRAL: [...defaultCraveScoreConfig],
  FITTING_ROOM: [...defaultCraveScoreConfig],
  MOBILE_PICKUP: [...defaultCraveScoreConfig],
  IN_AISLE: [
    {
      id: "sessions",
      label: i18n.t("storeComparison.fields.fittingRoomSessions"),
      enabled: false,
      weight: 1.0,
      goal: 100,
      direction: "higher",
    },
    {
      id: "ups",
      label: "Units Per Session",
      enabled: false,
      weight: 3,
      goal: 3.0,
      direction: "higher",
      normalizer: createAboveGoalNormalizer(UPS_FULL_CREDIT_MULTIPLIER),
    },
    { id: "missedPct", label: "Missed Requests %", weight: 3, goal: 6, direction: "lower" },
    {
      id: "avgResponseSec",
      label: "Avg Response Time (s)",
      weight: 3,
      goal: 90,
      direction: "lower",
    },

    // Requests Breakdown
    {
      id: "fulfilledPct",
      label: "Fulfilled Requests %",
      enabled: false,
      weight: 2.0,
      goal: 85,
      direction: "higher",
    },
    { id: "cantFindPct", label: "Can't Finds %", weight: 1.5, goal: 5, direction: "lower" },
    {
      id: "canceledPct",
      label: "Cancelled Requests %",
      enabled: false,
      weight: 1,
      goal: 3,
      direction: "lower",
    },
    {
      id: "totalRequests",
      label: "Total Requests",
      enabled: false,
      weight: 1.0,
      goal: 100,
      direction: "higher",
    },

    // Session Breakdown
    {
      id: "dwellSec",
      label: "Dwell Time (s)",
      enabled: false,
      weight: 1.0,
      goal: 300,
      direction: "higher",
    },
    {
      id: "namedShoppersPct",
      label: "Named Shoppers %",
      enabled: false,
      weight: 1.0,
      goal: 25,
      direction: "higher",
    },
    {
      id: "satisfaction",
      label: "Experience Rating",
      weight: 2,
      goal: 4.7,
      direction: "higher",
      normalizer: satisfactionNormalizer,
    },
  ],
};

export const getCraveScoreConfigForDevice = (
  deviceType: DeviceTypeForCrave | undefined | null,
): MetricConfig[] => {
  if (!deviceType || !(deviceType in deviceCraveScoreConfigs)) return defaultCraveScoreConfig;
  return deviceCraveScoreConfigs[deviceType];
};
